import React, { useContext, useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { AppContext } from '@state/context'
import { closePanel } from '@state/actions/panelsActions'
import Button from '@components/Button'
import ButtonWithLoader from '@components/ButtonWithLoader'
import Heading from '@components/Heading'
import Divider from '@components/Divider'
import Form from '@components/Form'
import {
  PANELS,
  DIVIDER_SIZE,
  BUTTON_SIZE,
  HEADING_TYPE,
  BUTTON_TYPE,
  TEXT_SIZES,
} from '@helpers/constants'
import Text from '@components/Text'
import { useCurrentUser } from '@hooks/useCurrentUser'
import { useSchool } from '@hooks/useSchool'
import Loader from '@components/Loader'
import { useUpdateCustomer } from '@hooks/mutations/useUpdateCustomer'
import { useDemoResources } from '@hooks/useDemoResources'
import { useUpdateDemoSchoolClass } from '@hooks/mutations/useUpdateDemoSchoolClass'

const AddClassPanel = () => {
  const [, dispatch] = useContext(AppContext)
  const [schoolClassOptions, setSchoolClassOptions] = useState()

  const { currentUser } = useCurrentUser()
  const { school, error, isLoading } = useSchool(currentUser?.schools?.[0]?.id)
  const { demoSchoolClasses } = useDemoResources({ id: currentUser?.id, connected: false })

  useEffect(() => {
    let options = []

    if (school && currentUser) {
      const usersClasses = currentUser.schoolClasses.map(sc => sc.id)
      const schoolClasses = currentUser.hasPaidLicense
        ? school.schoolClasses.filter((sc) => !sc.isPrivate)
        : school.schoolClasses.filter((sc) => sc.isPrivate && (sc?.customer?.id === currentUser.id))

      options.push(...
        schoolClasses.filter((sc) => !usersClasses.includes(sc.id)).map((sc) => ({
          name: sc.name,
          value: sc.id,
        }))
      )
    }

    if (demoSchoolClasses) {
      options.push(...demoSchoolClasses.map((sc) => ({
        name: sc.name,
        value: sc.id,
        isDemo: true,
      })))
    }

    setSchoolClassOptions(options)
  }, [currentUser, isLoading, school, demoSchoolClasses])

  const { post, isPosting } = useUpdateCustomer()
  const { post: postUpdateDemoSchoolClass } = useUpdateDemoSchoolClass()
  const { handleSubmit, errors, control, formState } = useForm({ mode: 'onChange' })
  const { isValid } = formState

  const onSubmit = async (data) => {
    const demoClasses = data.schoolClasses.filter(sc => sc.isDemo)
    const classes = data.schoolClasses.filter(sc => !sc.isDemo)

    if (classes.length) {
      const res = await post({
        connectSchoolClasses: classes.map(sc => sc.value),
        id: currentUser.id
      })
      if (res.success) dispatch(closePanel(PANELS.ADD_SCHOOL_CLASS))
    }

    if (demoClasses.length) {
      demoClasses.forEach(async dc => {
        const res = await postUpdateDemoSchoolClass({
          connected: true,
          id: dc.value,
        },
          'Klassen lades till.'
        )
        if (res.success) dispatch(closePanel(PANELS.ADD_SCHOOL_CLASS))
      })
    }

  }
  if (currentUser?.schools?.length === 0) {
    return (
      <div className={'flex flex-col items-center'}>
        <Heading headingLevel={HEADING_TYPE.H2} size={TEXT_SIZES.XLARGE}>
          Lägg till klass
        </Heading>
        <Divider size={DIVIDER_SIZE.SMALL} />
        <Text>
          Din användare är inte kopplad till någon skola ännu. Om felet inte är
          åtgärdat inom kort får du gärna ta kontakt med oss.
        </Text>
      </div>
    )
  }

  return (
    <>
      <div className={'relative'}>
        <div className='absolute top-0 right-0'>
          <Button
            handleClick={() => {
              dispatch(closePanel(PANELS.ADD_SCHOOL_CLASS))
            }}
            size={BUTTON_SIZE.NO_PADDING}
            icon={'Close'}
          ></Button>
        </div>
      </div>

      <div className={'flex flex-col items-center'}>
        <Text>Mitt lärarrum - {school?.name ?? ''}</Text>
        <Heading headingLevel={HEADING_TYPE.H2} size={TEXT_SIZES.XLARGE}>
          Lägg till klass
        </Heading>
      </div>

      <Divider size={DIVIDER_SIZE.SMALL} />

      {!schoolClassOptions && isLoading && currentUser?.schools?.length > 0 && <Loader />}
      {error && <Text>Kunde inte ladda klasser</Text>}

      {schoolClassOptions && !error && (
        <Form onSubmit={handleSubmit(onSubmit)}>
          <Form.Label htmlFor='schoolClasses'>Välj klass</Form.Label>
          <Controller
            control={control}
            name={'schoolClasses'}
            defaultValue={[]}
            rules={{
              validate: (value) => value.length > 0,
            }}
            render={(
              { onChange, onBlur, value, name, ref },
              { invalid, isTouched, isDirty }
            ) => (
              <Form.MultiSelect
                name={name}
                onChange={(options) => onChange(options)}
                options={schoolClassOptions}
                placeholder={
                  schoolClassOptions.length === 0
                    ? 'Alla klasser är redan tillagda i ditt lärarrum'
                    : 'Välj klass'
                }
                isMulti
                isSearchable
                isDisabled={schoolClassOptions.length === 0}
              />
            )}
          />
          {errors.schoolClasses && (
            <Form.ErrorMessage>En eller flera klasser behövs</Form.ErrorMessage>
          )}

          <Divider size={DIVIDER_SIZE.SMALL} />

          <ButtonWithLoader
            type={BUTTON_TYPE.PRIMARY}
            isLoading={isPosting}
            disabledText={'Lägg till'}
            enabledText={'Lägg till'}
            disabled={!isValid || isPosting || schoolClassOptions.length === 0}
          />
        </Form>
      )}
    </>
  )
}

export default AddClassPanel
